import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import moment from 'moment';
import FileUploadDialog from '../../../core/components/FileUploadDialog';
import {
  TaskOccurrence,
  taskTemplatesApi,
  useArchiveOccurrenceFileMutation,
  useGetOccurrenceFilesQuery,
} from '../../../core/api/rtk/taskTemplatesApi';
import { postTaskOccurrenceFile } from '../../../core/api/files';
import { apiError } from '../../../core/toastMessages';
import { selectRole, selectUserGroup } from '../../../store/authReducer';
import { hasRole, RoleTypes } from '../../../core/components/Permission';
import {
  findPermissionRef,
  viewPermissionRefs,
} from '../../../core/components/UserGroupPermissions';

interface TaskAttachmentsTabProps {
  occurrence: TaskOccurrence | null | undefined;
}

export const TaskAttachmentsTab = ({ occurrence }: TaskAttachmentsTabProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const selectedSiteId = useSelector((state: any) => state?.auth?.selectedSite ?? null) as
    | number
    | null;
  const role = useSelector(selectRole);
  const userGroup = useSelector(selectUserGroup);
  const enhancedRole = findPermissionRef(
    (userGroup as any)?.permissions ?? [],
    viewPermissionRefs.admin
  )
    ? 'Admin'
    : role;
  const canModifyFiles = hasRole(enhancedRole, RoleTypes.SiteManager) && !!selectedSiteId;
  const [uploadOpen, setUploadOpen] = useState(false);

  const { data: occurrenceFiles = [] } = useGetOccurrenceFilesQuery(occurrence?.id as number, {
    skip: !occurrence?.id,
  });
  const [archiveFile] = useArchiveOccurrenceFileMutation();

  const handleAddFile = async (file: File): Promise<void> => {
    if (!selectedSiteId || !occurrence?.id) return;
    const formData = new FormData();
    formData.append('file', file, file.name);
    await postTaskOccurrenceFile({
      options: { siteId: selectedSiteId },
      data: { occurrenceId: occurrence.id, file: formData },
    });
    dispatch(
      taskTemplatesApi.util.invalidateTags([
        { type: 'TaskTemplate' as const, id: `occurrence-files-${occurrence.id}` },
      ])
    );
  };

  const handleDeleteFile = async (file: any): Promise<void> => {
    if (!occurrence?.id) return;
    try {
      await archiveFile({ occurrenceId: occurrence.id, fileId: file.id }).unwrap();
    } catch (e: any) {
      apiError(e?.data || 'Failed to remove file');
    }
  };

  if (!occurrence?.id) {
    return (
      <p style={{ color: '#64748b' }}>
        {intl.formatMessage({
          id: 'noOccurrenceForAttachments',
          defaultMessage: 'No active occurrence — attachments are unavailable.',
        })}
      </p>
    );
  }

  return (
    <div>
      {canModifyFiles && (
        <div style={{ marginBottom: '0.75rem' }}>
          <button className="button" type="button" onClick={() => setUploadOpen(true)}>
            + Upload
          </button>
          <FileUploadDialog
            isOpen={uploadOpen}
            onClose={() => setUploadOpen(false)}
            onSubmit={handleAddFile}
            submitButtonText={intl.formatMessage({ id: 'uploadAttachment' })}
            fileType={['image', 'video']}
            instructions={intl.formatMessage({ id: 'uploadPhotoVideoOnly' })}
            multipleFiles
          />
        </div>
      )}
      {occurrenceFiles.length === 0 ? (
        <p style={{ color: '#64748b' }}>
          {intl.formatMessage({
            id: 'noOccurrenceFiles',
            defaultMessage: 'No files attached yet.',
          })}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {occurrenceFiles.map((file) => {
            const when = file.createdAt ? moment(file.createdAt).format('MMM D, h:mma') : '';
            const actor = file.creator || 'system';
            return (
              <li key={file.id} style={{ padding: '0.5rem 0', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                  <a
                    href={file.downloadUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.9rem', fontWeight: 600, wordBreak: 'break-all' }}
                  >
                    {file.originalName || file.key}
                  </a>
                  <span style={{ color: '#64748b', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    {when}
                  </span>
                </div>
                <div style={{ color: '#475569', fontSize: '0.85rem', marginTop: '0.15rem' }}>
                  {actor}
                </div>
                {file.description && (
                  <div
                    style={{
                      color: '#475569',
                      fontSize: '0.85rem',
                      marginTop: '0.25rem',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {file.description}
                  </div>
                )}
                {canModifyFiles && (
                  <div style={{ marginTop: '0.25rem' }}>
                    <button
                      className="button transparent"
                      type="button"
                      onClick={() => handleDeleteFile(file)}
                      style={{ padding: 0, fontSize: '0.8rem', color: '#64748b' }}
                    >
                      {intl.formatMessage({ id: 'remove', defaultMessage: 'Remove' })}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
